import React from "react";
import { useNavigate } from "react-router-dom";
import { Star, MessageSquare, ThumbsUp, Lock } from "lucide-react";
import { TRAITS, scoreColor, scoreLabel } from "@/lib/traits";
import FavoriteButton from "@/components/FavoriteButton";

function initials(name) {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export default function SupervisorCard({ sup }) {
  const navigate = useNavigate();
  const topTraits = [...TRAITS]
    .map((t) => ({ ...t, score: sup.traits?.[t.key] || 0 }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  if (sup.ratings_hidden) {
    return (
      <button
        data-testid="supervisor-card-locked"
        onClick={() => navigate(`/supervisor/${sup.id}`)}
        className="relative text-left bg-slate-50 border border-dashed border-slate-300 rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-emerald-500/40 w-full group"
      >
        <FavoriteButton kind="supervisor" refId={sup.id} className="absolute top-3 right-3 z-10" size={16} />
        <div className="flex items-center gap-3 pr-10">
          <div className="h-12 w-12 rounded-lg bg-slate-200 text-slate-500 font-display flex items-center justify-center text-lg" style={{ fontWeight: 700 }}>
            {initials(sup.name)}
          </div>
          <div>
            <div className="font-display text-xl leading-tight text-slate-900" style={{ fontWeight: 700 }} data-testid="supervisor-card-name">
              {sup.name}
            </div>
            <div className="text-sm text-slate-500">{sup.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 text-xs font-mono uppercase tracking-wider text-slate-400">
          <span>{sup.company}</span>
          <span className="text-slate-300">•</span>
          <span>{sup.department}</span>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-200 flex items-start gap-2 text-sm text-slate-500">
          <Lock size={15} className="text-emerald-600 shrink-0 mt-0.5" />
          <span>
            <b className="text-slate-700">Ratings locked</b> — unlock at {sup.min_reviews} reviews to protect reviewer anonymity.
            <span className="block text-emerald-700 font-medium mt-1 group-hover:underline">Be one of the first to review</span>
          </span>
        </div>
      </button>
    );
  }

  return (
    <button
      data-testid="supervisor-card"
      onClick={() => navigate(`/supervisor/${sup.id}`)}
      className="relative text-left bg-white border border-slate-200 rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-emerald-500/40 w-full group"
    >
      <FavoriteButton kind="supervisor" refId={sup.id} className="absolute top-3 right-3 z-10" size={16} />
      <div className="flex items-start justify-between gap-3 pr-10">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-lg bg-slate-900 text-white font-display flex items-center justify-center text-lg group-hover:bg-emerald-600 transition-colors" style={{ fontWeight: 700 }}>
            {initials(sup.name)}
          </div>
          <div>
            <div className="font-display text-xl leading-tight text-slate-900" style={{ fontWeight: 700 }} data-testid="supervisor-card-name">
              {sup.name}
            </div>
            <div className="text-sm text-slate-500">{sup.title}</div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1 justify-end">
            <Star size={16} fill="#F59E0B" color="#F59E0B" />
            <span className="font-mono text-lg font-semibold text-slate-900">
              {sup.overall > 0 ? sup.overall.toFixed(1) : "—"}
            </span>
          </div>
          <div className="text-xs font-medium" style={{ color: scoreColor(sup.overall) }}>
            {scoreLabel(sup.overall)}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 text-xs font-mono uppercase tracking-wider text-slate-400">
        <span>{sup.company}</span>
        <span className="text-slate-300">•</span>
        <span>{sup.department}</span>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-4">
        {topTraits.map((t) => (
          <span
            key={t.key}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600"
          >
            <t.icon size={12} className="text-emerald-600" />
            {t.short}
            {t.score > 0 && <b className="font-mono text-slate-900">{t.score.toFixed(1)}</b>}
          </span>
        ))}
      </div>

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100 text-sm text-slate-500">
        <span className="flex items-center gap-1.5">
          <MessageSquare size={14} /> {sup.review_count} review{sup.review_count === 1 ? "" : "s"}
        </span>
        {sup.review_count > 0 && (
          <span className="flex items-center gap-1.5">
            <ThumbsUp size={14} /> {sup.recommend_rate}% recommend
          </span>
        )}
      </div>
    </button>
  );
}
